import { useParams, Link } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { mockBlogPosts } from '@/data/mockData';
import { Button } from '@/components/ui/button';
import { Calendar, User, ArrowLeft, BookOpen } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Card } from '@/components/ui/card';
import entretienHero from '@/assets/blog-entretien-hero.jpg';
import entretienPreparation from '@/assets/blog-entretien-preparation.jpg';
import questionsHero from '@/assets/blog-questions-hero.jpg';
import questionsPreparation from '@/assets/blog-questions-preparation.jpg';

const categoryLabels = {
  'conseils-cv': 'CV & Lettres',
  'preparation-entretiens': 'Entretiens',
  'developpement-competences': 'Compétences',
  'portraits-talents': 'Portraits',
  'innovation-tech': 'Innovation & Tech'
};

const categoryColors = {
  'conseils-cv': 'bg-primary',
  'preparation-entretiens': 'bg-secondary',
  'developpement-competences': 'bg-success',
  'portraits-talents': 'bg-accent',
  'innovation-tech': 'bg-cyan-500'
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = mockBlogPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold text-foreground mb-4">Article non trouvé</h1>
          <Link to="/blog">
            <Button variant="default">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour au blog
            </Button>
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  // Detailed content for the interview strategies article
  const isInterviewArticle = post.slug === 'strategies-reussir-entretien-embauche';
  const isQuestionsArticle = post.slug === 'questions-entretien-frequentes';
  const isAIArticle = post.slug === 'ia-developpement-brazzaville';

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <Link to="/blog">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour au blog
          </Button>
        </Link>

        <article className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className={`inline-block ${categoryColors[post.category]} text-white px-4 py-2 rounded-md text-sm font-semibold mb-4`}>
              {categoryLabels[post.category]}
            </div>
            
            <h1 className="text-4xl md:text-5xl font-poppins font-bold text-foreground mb-6 leading-tight">
              {post.title}
            </h1>

            <div className="flex items-center gap-6 text-muted-foreground">
              <div className="flex items-center gap-2">
                <User className="h-5 w-5" />
                <span className="font-inter font-medium">{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                <span className="font-inter">
                  {format(post.publishedAt, 'dd MMMM yyyy', { locale: fr })}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                <span className="font-inter">15 min de lecture</span>
              </div>
            </div>
          </div>

          {/* Hero Image */}
          {isInterviewArticle && (
            <div className="mb-12 rounded-xl overflow-hidden">
              <img 
                src={entretienHero} 
                alt="Entretien d'embauche professionnel" 
                className="w-full h-auto object-cover"
              />
            </div>
          )}
          {isQuestionsArticle && (
            <div className="mb-12 rounded-xl overflow-hidden">
              <img 
                src={questionsHero} 
                alt="Préparation aux questions d'entretien" 
                className="w-full h-auto object-cover"
              />
            </div>
          )}

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            {post.slug === 'comment-rediger-cv-percutant-2024' ? (
              <>
                <p className="text-muted-foreground mb-6">
                  Votre CV (Curriculum Vitae) est votre outil le plus important pour décrocher un entretien. C'est un document qui présente votre expérience professionnelle, votre formation, vos compétences et vos réalisations. Dans un marché du travail compétitif, un CV bien rédigé peut faire toute la différence.
                </p>

                <h2 className="text-2xl font-bold mb-4 mt-8">Les différents types de CV</h2>
                
                <h3 className="text-xl font-semibold mb-3 mt-6">1. Le CV Traditionnel</h3>
                <p className="text-muted-foreground mb-4">
                  Le CV traditionnel est idéal pour les secteurs conservateurs comme la comptabilité ou la banque. Il se caractérise par :
                </p>
                <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-2">
                  <li>Des sections bien séparées</li>
                  <li>Une présentation professionnelle et intemporelle</li>
                  <li>Une navigation facile pour les recruteurs</li>
                </ul>

                <h3 className="text-xl font-semibold mb-3 mt-6">2. Le CV Moderne</h3>
                <p className="text-muted-foreground mb-4">
                  Le CV moderne est conçu pour vous démarquer. Il comprend :
                </p>
                <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-2">
                  <li>Une barre de couleur en haut qui attire l'attention</li>
                  <li>Votre nom et titre professionnel bien visibles</li>
                  <li>Des sections séparées par des titres stylisés</li>
                  <li>Beaucoup d'espace blanc pour une lecture facile</li>
                </ul>

                <h3 className="text-xl font-semibold mb-3 mt-6">3. Le CV à 2 Colonnes</h3>
                <p className="text-muted-foreground mb-4">
                  Ce format combine style et fonctionnalité :
                </p>
                <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-2">
                  <li>Une colonne latérale pour vos coordonnées et compétences clés</li>
                  <li>Idéal pour condenser votre CV sur une page</li>
                  <li>Parfait pour les métiers créatifs</li>
                </ul>

                <h3 className="text-xl font-semibold mb-3 mt-6">4. Le CV Simple</h3>
                <p className="text-muted-foreground mb-4">
                  Le CV simple est une version modernisée du CV traditionnel :
                </p>
                <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-2">
                  <li>Sections clairement divisées avec des titres en gras</li>
                  <li>Quelques éléments de design subtils (icônes, lignes colorées)</li>
                  <li>Équilibre entre professionnalisme et modernité</li>
                </ul>

                <Card className="my-6 p-6 bg-primary/5 border-primary/20">
                  <h4 className="font-semibold mb-2">💡 Conseil</h4>
                  <p className="text-sm text-muted-foreground">
                    Choisissez un format qui correspond à l'entreprise visée. Les organisations conservatrices préfèrent les formats traditionnels, tandis que les entreprises modernes apprécient les designs plus créatifs.
                  </p>
                </Card>

                <h2 className="text-2xl font-bold mb-4 mt-8">Les éléments essentiels d'un CV</h2>

                <h3 className="text-xl font-semibold mb-3 mt-6">1. Informations de contact</h3>
                <p className="text-muted-foreground mb-4">
                  Incluez toujours :
                </p>
                <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-2">
                  <li>Votre nom complet</li>
                  <li>Numéro de téléphone</li>
                  <li>Adresse email professionnelle</li>
                  <li>Localisation (ville, arrondissement)</li>
                  <li>Profil LinkedIn (optionnel)</li>
                </ul>

                <h3 className="text-xl font-semibold mb-3 mt-6">2. Résumé professionnel</h3>
                <p className="text-muted-foreground mb-4">
                  Un court paragraphe de 3 à 5 phrases qui présente :
                </p>
                <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-2">
                  <li>Votre titre professionnel</li>
                  <li>Vos compétences principales</li>
                  <li>Vos objectifs de carrière</li>
                  <li>Ce que vous pouvez apporter à l'entreprise</li>
                </ul>

                <h3 className="text-xl font-semibold mb-3 mt-6">3. Expérience professionnelle</h3>
                <p className="text-muted-foreground mb-4">
                  Pour chaque poste, indiquez :
                </p>
                <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-2">
                  <li>Le titre du poste</li>
                  <li>Le nom de l'entreprise</li>
                  <li>La localisation et les dates</li>
                  <li>Vos responsabilités et réalisations en points</li>
                  <li>Commencez chaque point par un verbe d'action fort</li>
                </ul>

                <Card className="my-6 p-6">
                  <h4 className="font-semibold mb-3">Exemple de bons verbes d'action :</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-muted-foreground">
                    <div>• Développé</div>
                    <div>• Dirigé</div>
                    <div>• Optimisé</div>
                    <div>• Créé</div>
                    <div>• Géré</div>
                    <div>• Amélioré</div>
                    <div>• Coordonné</div>
                    <div>• Réalisé</div>
                    <div>• Augmenté</div>
                  </div>
                </Card>

                <h3 className="text-xl font-semibold mb-3 mt-6">4. Formation</h3>
                <p className="text-muted-foreground mb-4">
                  Indiquez :
                </p>
                <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-2">
                  <li>Vos diplômes et spécialités</li>
                  <li>Les noms des établissements</li>
                  <li>Les dates d'obtention</li>
                  <li>Les mentions et distinctions (optionnel)</li>
                  <li>Les cours pertinents (pour les jeunes diplômés)</li>
                </ul>

                <h3 className="text-xl font-semibold mb-3 mt-6">5. Compétences</h3>
                <p className="text-muted-foreground mb-4">
                  Créez une section dédiée pour :
                </p>
                <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-2">
                  <li>Les compétences techniques (logiciels, outils)</li>
                  <li>Les compétences linguistiques</li>
                  <li>Les compétences interpersonnelles</li>
                </ul>

                <h3 className="text-xl font-semibold mb-3 mt-6">6. Sections supplémentaires (optionnel)</h3>
                <p className="text-muted-foreground mb-4">
                  Selon votre profil, ajoutez :
                </p>
                <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-2">
                  <li>Certifications professionnelles</li>
                  <li>Projets personnels ou académiques</li>
                  <li>Expérience de bénévolat</li>
                  <li>Publications ou recherches</li>
                  <li>Activités extra-professionnelles</li>
                </ul>

                <h2 className="text-2xl font-bold mb-4 mt-8">Conseils de mise en forme</h2>

                <h3 className="text-xl font-semibold mb-3 mt-6">Design et présentation</h3>
                <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-2">
                  <li>Utilisez une police professionnelle (Arial, Calibri) en taille 11-12pt</li>
                  <li>Créez des sections avec des titres en gras</li>
                  <li>Laissez suffisamment d'espace blanc pour faciliter la lecture</li>
                  <li>Enregistrez toujours votre CV en format PDF</li>
                  <li>Limitez votre CV à 1-2 pages maximum</li>
                </ul>

                <h3 className="text-xl font-semibold mb-3 mt-6">Structure du contenu</h3>
                <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-2">
                  <li>Commencez chaque section sur une nouvelle ligne</li>
                  <li>Mettez les expériences les plus récentes en premier</li>
                  <li>Utilisez des puces pour lister vos réalisations</li>
                  <li>Soyez cohérent dans votre formatage</li>
                </ul>

                <Card className="my-6 p-6 bg-primary/5 border-primary/20">
                  <h4 className="font-semibold mb-2">⏱️ Bon à savoir</h4>
                  <p className="text-sm text-muted-foreground">
                    Les recruteurs passent en moyenne 6-7 secondes à regarder un CV lors du premier tri. Votre mise en forme doit donc permettre une lecture rapide et facile.
                  </p>
                </Card>

                <h2 className="text-2xl font-bold mb-4 mt-8">Personnaliser votre CV</h2>

                <p className="text-muted-foreground mb-4">
                  La personnalisation de votre CV pour chaque candidature est cruciale. Un CV générique est facilement identifiable par les recruteurs.
                </p>

                <h3 className="text-xl font-semibold mb-3 mt-6">Étapes pour personnaliser votre CV :</h3>

                <div className="space-y-4 mb-6">
                  <Card className="p-4">
                    <h4 className="font-semibold mb-2">1. Recherchez l'entreprise et le poste</h4>
                    <p className="text-sm text-muted-foreground">
                      Étudiez attentivement l'offre d'emploi pour identifier les compétences, qualifications et mots-clés importants. Visitez le site web de l'entreprise pour comprendre sa culture.
                    </p>
                  </Card>

                  <Card className="p-4">
                    <h4 className="font-semibold mb-2">2. Adaptez votre résumé professionnel</h4>
                    <p className="text-sm text-muted-foreground">
                      Rédigez un résumé qui reflète vos qualifications les plus pertinentes pour ce poste spécifique. Montrez comment vos objectifs s'alignent avec la mission de l'entreprise.
                    </p>
                  </Card>

                  <Card className="p-4">
                    <h4 className="font-semibold mb-2">3. Mettez en avant les expériences pertinentes</h4>
                    <p className="text-sm text-muted-foreground">
                      Concentrez-vous sur les rôles et responsabilités directement liés au poste. Vous n'avez pas besoin d'inclure tous vos emplois passés.
                    </p>
                  </Card>

                  <Card className="p-4">
                    <h4 className="font-semibold mb-2">4. Utilisez les bons mots-clés</h4>
                    <p className="text-sm text-muted-foreground">
                      Les systèmes de tri automatique (ATS) recherchent des mots-clés spécifiques. Incluez la terminologie utilisée dans l'offre d'emploi pour augmenter vos chances de passer ce premier filtre.
                    </p>
                  </Card>

                  <Card className="p-4">
                    <h4 className="font-semibold mb-2">5. Créez des sections sur mesure</h4>
                    <p className="text-sm text-muted-foreground">
                      Selon le poste, ajoutez des sections comme "Cours pertinents" pour les postes junior ou "Expérience en leadership" pour les postes de management.
                    </p>
                  </Card>
                </div>

                <h2 className="text-2xl font-bold mb-4 mt-8">Erreurs courantes à éviter</h2>

                <div className="space-y-4 mb-6">
                  <Card className="p-4 border-destructive/30">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      ❌ Fautes d'orthographe et de grammaire
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Un CV avec des fautes peut ruiner vos chances. Relisez plusieurs fois et faites relire par un ami. Utilisez des outils comme un correcteur orthographique.
                    </p>
                  </Card>

                  <Card className="p-4 border-destructive/30">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      ❌ Adresse email non professionnelle
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Évitez les adresses comme "coolguy123@gmail.com". Utilisez plutôt le format [Prénom][Nom]@gmail.com pour une image professionnelle.
                    </p>
                  </Card>

                  <Card className="p-4 border-destructive/30">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      ❌ Informations de contact incorrectes
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Vérifiez toujours que votre numéro de téléphone et votre email sont corrects et à jour. Une erreur ici signifie qu'on ne pourra pas vous contacter.
                    </p>
                  </Card>

                  <Card className="p-4 border-destructive/30">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      ❌ Informations non pertinentes
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Ne listez pas simplement vos tâches passées. Concentrez-vous sur vos réalisations et résultats mesurables. Au lieu de "géré une équipe", écrivez "dirigé une équipe de 10 personnes qui a dépassé les objectifs de vente de 20%".
                    </p>
                  </Card>

                  <Card className="p-4 border-destructive/30">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      ❌ Ne pas personnaliser le CV
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Envoyer le même CV générique pour tous les postes n'est pas efficace. La personnalisation montre votre intérêt réel pour le poste.
                    </p>
                  </Card>

                  <Card className="p-4 border-destructive/30">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      ❌ Exagérer ou mentir
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Soyez toujours honnête sur vos qualifications et expériences. Les mensonges peuvent avoir des conséquences graves, y compris le rejet de votre candidature.
                    </p>
                  </Card>

                  <Card className="p-4 border-destructive/30">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      ❌ Utiliser des verbes faibles
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Évitez les verbes comme "travaillé" ou "fait". Utilisez plutôt des verbes d'action forts comme "développé", "dirigé", "optimisé" pour montrer l'impact de vos contributions.
                    </p>
                  </Card>
                </div>

                <h2 className="text-2xl font-bold mb-4 mt-8">Conseils pour les jeunes diplômés</h2>

                <p className="text-muted-foreground mb-4">
                  Si vous avez peu d'expérience professionnelle, ne vous inquiétez pas. Voici comment créer un CV convaincant :
                </p>

                <h3 className="text-xl font-semibold mb-3 mt-6">1. Commencez par un résumé fort</h3>
                <p className="text-muted-foreground mb-4">
                  Mettez en avant vos compétences clés et vos objectifs de carrière. Montrez votre enthousiasme et votre potentiel.
                </p>

                <h3 className="text-xl font-semibold mb-3 mt-6">2. Mettez en avant vos compétences transférables</h3>
                <p className="text-muted-foreground mb-4">
                  Les compétences transférables sont celles qui peuvent s'appliquer à différents emplois :
                </p>
                <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-2">
                  <li>Communication</li>
                  <li>Travail d'équipe</li>
                  <li>Résolution de problèmes</li>
                  <li>Organisation</li>
                  <li>Gestion du temps</li>
                </ul>

                <h3 className="text-xl font-semibold mb-3 mt-6">3. Incluez vos projets et cours pertinents</h3>
                <p className="text-muted-foreground mb-4">
                  Détaillez les projets académiques importants, les stages, ou les projets personnels qui démontrent vos capacités. Mentionnez les cours directement liés au poste.
                </p>

                <h3 className="text-xl font-semibold mb-3 mt-6">4. Mentionnez vos activités extra-scolaires</h3>
                <p className="text-muted-foreground mb-4">
                  La participation à des clubs, associations, sports ou organisations étudiantes montre votre leadership, votre engagement et votre capacité à travailler en équipe.
                </p>

                <h3 className="text-xl font-semibold mb-3 mt-6">5. Créez une section compétences détaillée</h3>
                <p className="text-muted-foreground mb-4">
                  Listez vos compétences techniques (logiciels, langues, outils) et adaptez-les aux exigences du poste.
                </p>

                <h3 className="text-xl font-semibold mb-3 mt-6">6. Expliquez les périodes d'inactivité positivement</h3>
                <p className="text-muted-foreground mb-4">
                  Si vous avez des périodes sans emploi, mentionnez toute activité productive : bénévolat, auto-formation, cours en ligne, développement personnel.
                </p>

                <Card className="my-6 p-6 bg-primary/5 border-primary/20">
                  <h4 className="font-semibold mb-2">En résumé</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Un CV percutant nécessite :
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Un format adapté au secteur visé</li>
                    <li>• Des informations claires et bien organisées</li>
                    <li>• Une personnalisation pour chaque candidature</li>
                    <li>• Des réalisations concrètes plutôt que des tâches</li>
                    <li>• Une présentation sans fautes et professionnelle</li>
                    <li>• Des verbes d'action forts et des résultats mesurables</li>
                  </ul>
                </Card>

                <div className="mt-8 p-6 bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg border border-primary/20">
                  <h3 className="text-xl font-semibold mb-3">Prêt à rédiger votre CV ?</h3>
                  <p className="text-muted-foreground mb-4">
                    Maintenant que vous connaissez toutes les bonnes pratiques, prenez le temps de créer ou d'améliorer votre CV. N'oubliez pas : un bon CV est celui qui est personnalisé, clair, sans fautes et qui met en valeur vos réalisations concrètes.
                  </p>
                  <p className="text-muted-foreground">
                    Bonne chance dans votre recherche d'emploi !
                  </p>
                </div>
              </>
            ) : isInterviewArticle ? (
              <>
                <p className="text-xl text-muted-foreground leading-relaxed mb-8">
                  {post.excerpt}
                </p>

                <Card className="bg-primary/5 border-primary/20 p-6 mb-8">
                  <p className="text-foreground leading-relaxed italic">
                    L'entretien d'embauche constitue un moment critique dans la trajectoire professionnelle d'un individu, 
                    représentant la convergence entre les aspirations du candidat et les exigences organisationnelles de l'entité employeuse.
                  </p>
                </Card>

                <div className="bg-muted p-8 rounded-lg text-center">
                  <p className="text-foreground">
                    Contenu complet de l'article sur les stratégies d'entretien...
                  </p>
                </div>
              </>
            ) : isQuestionsArticle ? (
              <>
                <p className="text-xl text-muted-foreground leading-relaxed mb-8">
                  {post.excerpt}
                </p>

                <Card className="bg-primary/5 border-primary/20 p-6 mb-8">
                  <p className="text-foreground leading-relaxed">
                    Les recruteurs posent souvent les mêmes questions en entretien. Bien se préparer à ces questions vous donnera 
                    un grand avantage. Voici les 10 questions les plus fréquentes et comment y répondre simplement et efficacement.
                  </p>
                </Card>

                <h2 className="text-3xl font-bold text-foreground mt-12 mb-6">
                  1. "Parlez-moi de vous"
                </h2>

                <p className="text-foreground leading-relaxed mb-4">
                  <strong>Ce que le recruteur cherche :</strong> Il veut savoir si vous savez résumer votre parcours et faire le lien avec le poste.
                </p>

                <Card className="bg-card border-border p-6 mb-8">
                  <h4 className="font-bold text-foreground mb-3">❌ À éviter</h4>
                  <p className="text-foreground mb-3">Raconter toute votre vie depuis l'enfance ou parler de choses personnelles sans lien avec le travail.</p>
                  
                  <h4 className="font-bold text-foreground mb-3 mt-4">✅ Bonne réponse</h4>
                  <p className="text-foreground italic">
                    "Actuellement, je travaille comme [votre poste] chez [entreprise], où je m'occupe de [vos responsabilités]. 
                    Avant cela, j'ai travaillé sur [expériences importantes]. Ce qui m'intéresse dans ce poste, c'est que je peux 
                    utiliser mes compétences en [X et Y] pour vous aider à [objectif de l'entreprise]."
                  </p>
                </Card>

                <h2 className="text-3xl font-bold text-foreground mt-12 mb-6">
                  2. "Quelles sont vos forces ?"
                </h2>

                <p className="text-foreground leading-relaxed mb-4">
                  <strong>Ce que le recruteur cherche :</strong> Il veut savoir si vous connaissez vos qualités et si elles correspondent au poste.
                </p>

                <Card className="bg-card border-border p-6 mb-8">
                  <h4 className="font-bold text-foreground mb-3">❌ À éviter</h4>
                  <p className="text-foreground mb-3">Dire "Je suis rigoureux et organisé" sans donner d'exemples concrets.</p>
                  
                  <h4 className="font-bold text-foreground mb-3 mt-4">✅ Bonne réponse</h4>
                  <p className="text-foreground italic">
                    "Je suis très bon en analyse de données. Par exemple, dans mon poste actuel, j'ai créé un tableau de bord 
                    qui a permis à l'équipe de prendre des décisions 40% plus vite. Je pense que cette compétence sera utile 
                    pour ce poste."
                  </p>
                </Card>

                <img 
                  src={questionsPreparation} 
                  alt="Préparation et confiance pour l'entretien" 
                  className="w-full h-auto rounded-lg mb-8"
                />

                <h2 className="text-3xl font-bold text-foreground mt-12 mb-6">
                  3. "Quelle est votre plus grande faiblesse ?"
                </h2>

                <p className="text-foreground leading-relaxed mb-4">
                  <strong>Ce que le recruteur cherche :</strong> Il veut voir si vous êtes honnête et si vous travaillez sur vos points faibles.
                </p>

                <Card className="bg-card border-border p-6 mb-8">
                  <h4 className="font-bold text-foreground mb-3">❌ À éviter</h4>
                  <ul className="text-foreground space-y-2 mb-3">
                    <li>• "Je suis trop perfectionniste" (fausse faiblesse)</li>
                    <li>• "Je ne sais pas gérer mon temps" (trop grave)</li>
                    <li>• "Je n'ai pas de faiblesse" (pas honnête)</li>
                  </ul>
                  
                  <h4 className="font-bold text-foreground mb-3 mt-4">✅ Bonne réponse</h4>
                  <p className="text-foreground italic">
                    "Parfois, j'ai du mal à prendre la parole rapidement en réunion parce que je veux être sûr de ce que je dis. 
                    J'ai remarqué que cela ralentissait mes contributions. Maintenant, je me force à poser au moins une question 
                    dans les 15 premières minutes de chaque réunion. Ça m'aide beaucoup."
                  </p>
                </Card>

                <h2 className="text-3xl font-bold text-foreground mt-12 mb-6">
                  4. "Pourquoi voulez-vous travailler ici ?"
                </h2>

                <p className="text-foreground leading-relaxed mb-4">
                  <strong>Ce que le recruteur cherche :</strong> Il veut savoir si vous avez vraiment préparé l'entretien et si vous êtes sincèrement intéressé.
                </p>

                <Card className="bg-card border-border p-6 mb-8">
                  <h4 className="font-bold text-foreground mb-3">❌ À éviter</h4>
                  <p className="text-foreground mb-3">"Votre entreprise est connue" ou "Je veux juste un travail".</p>
                  
                  <h4 className="font-bold text-foreground mb-3 mt-4">✅ Bonne réponse</h4>
                  <p className="text-foreground italic">
                    "J'aime vraiment votre approche de [quelque chose de spécifique]. Par exemple, j'ai vu que vous avez lancé 
                    [projet récent], et ça correspond exactement à ma vision. De plus, vos valeurs comme [valeur] correspondent 
                    à ce qui est important pour moi."
                  </p>
                </Card>

                <h2 className="text-3xl font-bold text-foreground mt-12 mb-6">
                  5. "Où vous voyez-vous dans 5 ans ?"
                </h2>

                <p className="text-foreground leading-relaxed mb-4">
                  <strong>Ce que le recruteur cherche :</strong> Il veut savoir si vous avez des objectifs et s'ils correspondent à ce que l'entreprise peut offrir.
                </p>

                <Card className="bg-card border-border p-6 mb-8">
                  <h4 className="font-bold text-foreground mb-3">❌ À éviter</h4>
                  <ul className="text-foreground space-y-2 mb-3">
                    <li>• "Je veux être directeur" (trop ambitieux)</li>
                    <li>• "Je ne sais pas" (pas de vision)</li>
                    <li>• "Je veux créer ma propre entreprise" (vous allez partir)</li>
                  </ul>
                  
                  <h4 className="font-bold text-foreground mb-3 mt-4">✅ Bonne réponse</h4>
                  <p className="text-foreground italic">
                    "Dans 5 ans, j'aimerais être un expert reconnu en [domaine] et avoir plus de responsabilités. 
                    J'aimerais aussi encadrer une petite équipe. Ce qui compte pour moi, c'est de vraiment bien faire mon 
                    travail et d'avoir un impact visible."
                  </p>
                </Card>

                <h2 className="text-3xl font-bold text-foreground mt-12 mb-6">
                  6. "Parlez-moi d'un conflit que vous avez géré"
                </h2>

                <p className="text-foreground leading-relaxed mb-4">
                  <strong>Ce que le recruteur cherche :</strong> Il veut voir comment vous gérez les situations difficiles avec les autres.
                </p>

                <Card className="bg-card border-border p-6 mb-8">
                  <h4 className="font-bold text-foreground mb-3">❌ À éviter</h4>
                  <ul className="text-foreground space-y-2 mb-3">
                    <li>• "Je n'ai jamais eu de conflit"</li>
                    <li>• Blâmer l'autre personne</li>
                    <li>• Raconter un conflit non résolu</li>
                  </ul>
                  
                  <h4 className="font-bold text-foreground mb-3 mt-4">✅ Bonne réponse</h4>
                  <p className="text-foreground italic">
                    "Dans mon ancien poste, l'équipe technique et l'équipe marketing n'étaient pas d'accord sur les priorités. 
                    J'ai organisé une réunion où chaque équipe devait expliquer ses contraintes. Nous avons découvert plusieurs 
                    malentendus. J'ai proposé une méthode de priorisation claire. Résultat : nous avons trouvé un accord et 
                    cette méthode est maintenant utilisée pour tous les projets."
                  </p>
                </Card>

                <h2 className="text-3xl font-bold text-foreground mt-12 mb-6">
                  7. "Parlez-moi d'un échec professionnel"
                </h2>

                <p className="text-foreground leading-relaxed mb-4">
                  <strong>Ce que le recruteur cherche :</strong> Il veut voir si vous acceptez vos erreurs et si vous en tirez des leçons.
                </p>

                <Card className="bg-card border-border p-6 mb-8">
                  <h4 className="font-bold text-foreground mb-3">❌ À éviter</h4>
                  <ul className="text-foreground space-y-2 mb-3">
                    <li>• Raconter un petit échec sans importance</li>
                    <li>• Dire que c'était la faute des autres</li>
                    <li>• Ne pas parler de ce que vous avez appris</li>
                  </ul>
                  
                  <h4 className="font-bold text-foreground mb-3 mt-4">✅ Bonne réponse</h4>
                  <p className="text-foreground italic">
                    "Quand j'ai commencé à gérer une équipe, j'ai voulu protéger mes collègues de la pression. J'ai donc 
                    attendu trop longtemps avant de signaler un problème technique à ma direction. Quand j'en ai parlé, 
                    c'était trop tard pour bien le régler. J'ai appris qu'il faut toujours communiquer tôt sur les problèmes. 
                    Maintenant, je partage tout de suite les risques, et ça marche beaucoup mieux."
                  </p>
                </Card>

                <h2 className="text-3xl font-bold text-foreground mt-12 mb-6">
                  8. "Pourquoi quittez-vous votre emploi actuel ?"
                </h2>

                <p className="text-foreground leading-relaxed mb-4">
                  <strong>Ce que le recruteur cherche :</strong> Il veut comprendre vos motivations sans entendre de critiques négatives.
                </p>

                <Card className="bg-card border-border p-6 mb-8">
                  <h4 className="font-bold text-foreground mb-3">❌ À éviter</h4>
                  <ul className="text-foreground space-y-2 mb-3">
                    <li>• Critiquer votre patron ou vos collègues</li>
                    <li>• Dire que c'est juste pour l'argent</li>
                    <li>• Parler de problèmes personnels</li>
                  </ul>
                  
                  <h4 className="font-bold text-foreground mb-3 mt-4">✅ Bonne réponse</h4>
                  <p className="text-foreground italic">
                    "J'ai beaucoup appris dans mon poste actuel, surtout en [domaines]. Mais après [durée], je veux maintenant 
                    me développer dans [nouvelle direction]. Mon entreprise actuelle se concentre sur [autre chose], donc les 
                    opportunités sont limitées. Votre entreprise, avec son focus sur [ce qui vous intéresse], est exactement 
                    ce que je cherche."
                  </p>
                </Card>

                <h2 className="text-3xl font-bold text-foreground mt-12 mb-6">
                  9. "Avez-vous d'autres opportunités en cours ?"
                </h2>

                <p className="text-foreground leading-relaxed mb-4">
                  <strong>Ce que le recruteur cherche :</strong> Il veut savoir si vous êtes recherché et si vous êtes honnête.
                </p>

                <Card className="bg-card border-border p-6 mb-8">
                  <h4 className="font-bold text-foreground mb-3">✅ Si oui :</h4>
                  <p className="text-foreground italic mb-4">
                    "Oui, je suis en discussion avec quelques entreprises. Mais votre opportunité m'intéresse particulièrement 
                    à cause de [raisons]. Si je reçois une offre ailleurs, je vous préviendrai avant de décider."
                  </p>
                  
                  <h4 className="font-bold text-foreground mb-3">✅ Si non :</h4>
                  <p className="text-foreground italic">
                    "Je fais ma recherche de manière ciblée. Je préfère me concentrer sur quelques opportunités de qualité 
                    qui correspondent vraiment à ce que je veux, plutôt que postuler partout."
                  </p>
                </Card>

                <h2 className="text-3xl font-bold text-foreground mt-12 mb-6">
                  10. "Avez-vous des questions pour nous ?"
                </h2>

                <p className="text-foreground leading-relaxed mb-4">
                  <strong>Ce que le recruteur cherche :</strong> Il veut voir si vous êtes vraiment intéressé et si vous avez réfléchi au poste.
                </p>

                <Card className="bg-secondary/5 border-secondary/20 p-6 mb-8">
                  <p className="text-foreground mb-3">
                    <strong>Important :</strong> Ne pas poser de questions montre que vous n'êtes pas vraiment intéressé !
                  </p>
                </Card>

                <div className="bg-muted p-6 rounded-lg mb-8">
                  <h4 className="font-bold text-foreground mb-4">❌ Questions à éviter :</h4>
                  <ul className="text-foreground space-y-2 mb-6">
                    <li>• Questions dont la réponse est sur le site web</li>
                    <li>• "Combien de jours de congé ?" (trop tôt)</li>
                    <li>• Questions fermées (oui/non)</li>
                  </ul>

                  <h4 className="font-bold text-foreground mb-4">✅ Bonnes questions :</h4>
                  <ul className="text-foreground space-y-2">
                    <li>• "Quels seraient mes objectifs prioritaires dans les 3 premiers mois ?"</li>
                    <li>• "Quels sont les plus grands défis de l'équipe actuellement ?"</li>
                    <li>• "Comment décririez-vous les personnes qui réussissent bien ici ?"</li>
                    <li>• "Comment sera évalué mon succès après un an ?"</li>
                    <li>• "Qu'est-ce qui vous plaît le plus dans votre travail ici ?"</li>
                  </ul>
                </div>

                <h2 className="text-3xl font-bold text-foreground mt-12 mb-6">
                  Résumé : Comment bien se préparer
                </h2>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <Card className="p-6 bg-success/5 border-success/20">
                    <h4 className="font-bold text-foreground mb-4">Avant l'entretien</h4>
                    <ul className="space-y-2 text-foreground">
                      <li>• Préparez vos réponses aux 10 questions</li>
                      <li>• Trouvez des exemples concrets de votre travail</li>
                      <li>• Renseignez-vous sur l'entreprise</li>
                      <li>• Préparez 5-7 questions à poser</li>
                      <li>• Dormez bien la veille</li>
                    </ul>
                  </Card>
                  <Card className="p-6 bg-primary/5 border-primary/20">
                    <h4 className="font-bold text-foreground mb-4">💡 Pendant l'entretien</h4>
                    <ul className="space-y-2 text-foreground">
                      <li>• Arrivez 10-15 minutes en avance</li>
                      <li>• Souriez et regardez la personne</li>
                      <li>• Écoutez bien les questions</li>
                      <li>• Prenez votre temps pour répondre</li>
                      <li>• Soyez vous-même, restez naturel</li>
                    </ul>
                  </Card>
                </div>

                <Card className="bg-primary/10 border-primary p-8 mb-8">
                  <h3 className="text-2xl font-bold text-foreground mb-4">
                    Le secret de la réussite
                  </h3>
                  <p className="text-foreground leading-relaxed text-lg">
                    Les recruteurs ne cherchent pas des candidats parfaits. Ils cherchent des personnes honnêtes, préparées, 
                    et qui montrent une vraie motivation. Si vous vous préparez bien à ces 10 questions et que vous restez 
                    authentique, vous aurez toutes les chances de réussir !
                  </p>
                </Card>

                <div className="bg-muted p-8 rounded-lg text-center mt-12">
                  <h3 className="text-2xl font-bold text-foreground mb-4">
                    Prêt à décrocher votre prochain emploi ?
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Découvrez des milliers d'opportunités à Brazzaville
                  </p>
                  <Link to="/opportunites">
                    <Button size="lg" className="font-semibold">
                      Voir les opportunités
                    </Button>
                  </Link>
                </div>
              </>
            ) : post.slug === 'ia-developpement-brazzaville' ? (
              <>
                <p className="text-muted-foreground text-lg mb-6 leading-relaxed">
                  L'Intelligence Artificielle (IA) émerge comme un axe de développement crucial pour Brazzaville, capitale de la République du Congo. Cette évolution reflète une tendance mondiale où les pays reconnaissent de plus en plus le potentiel de l'IA pour stimuler le progrès socio-économique, particulièrement dans les régions à revenu faible et intermédiaire.
                </p>

                <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Résumé</h2>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Alors que la République du Congo cherche à diversifier son économie et à améliorer ses capacités technologiques, des initiatives telles que la création du Centre Africain de Recherche en Intelligence Artificielle (CARIA) et du Centre de Recherche Africain pour l'Intelligence Artificielle (ARCAI) témoignent d'un engagement à intégrer l'IA dans divers secteurs, notamment la santé, l'agriculture et l'éducation.
                </p>

                <Card className="my-8 p-6 bg-primary/5 border-primary/20">
                  <h4 className="font-semibold mb-3 text-foreground">🎯 Points Clés</h4>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• L'IA offre des opportunités pour résoudre les problèmes locaux comme la pauvreté et l'allocation inefficace des ressources</li>
                    <li>• Des défis importants subsistent : infrastructure limitée, pénurie de professionnels qualifiés, disparités socio-économiques</li>
                    <li>• L'intégration des connaissances locales et des contextes culturels est essentielle</li>
                  </ul>
                </Card>

                <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Contexte Historique</h2>
                <p className="text-muted-foreground mb-4 leading-relaxed">
                  L'évolution de l'intelligence artificielle et son application en République du Congo, particulièrement à Brazzaville, est marquée par une interaction complexe entre les tendances technologiques mondiales et les besoins de développement locaux. Le concept d'IA, officiellement créé en 1956, a des racines qui remontent aux théories fondamentales proposées par des figures comme Alan Turing.
                </p>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Au XXIe siècle, l'avancement rapide de la technologie de l'IA a attiré l'attention des pays du monde entier, particulièrement dans le contexte de la Quatrième Révolution Industrielle. Bien que le développement précoce de l'IA ait été principalement concentré dans les nations occidentales, il y a eu un intérêt croissant pour exploiter les technologies de l'IA dans le Sud global, y compris l'Afrique, l'Asie du Sud-Est et l'Amérique latine.
                </p>

                <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">État Actuel de l'IA à Brazzaville</h2>
                
                <h3 className="text-2xl font-semibold text-foreground mb-4 mt-8">Vue d'Ensemble</h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  La République du Congo reconnaît progressivement l'importance de l'intelligence artificielle comme composante vitale pour améliorer son secteur technologique, en particulier dans sa capitale, Brazzaville. Bien que les initiatives spécifiques en IA ne soient pas largement documentées, le gouvernement s'est engagé à améliorer le paysage numérique et à exploiter les technologies émergentes pour le développement socio-économique.
                </p>

                <h3 className="text-2xl font-semibold text-foreground mb-4 mt-8">Initiatives Clés</h3>
                <div className="space-y-4 mb-8">
                  <Card className="p-6">
                    <h4 className="font-bold text-lg text-foreground mb-3">Centre Africain de Recherche en Intelligence Artificielle (CARIA)</h4>
                    <p className="text-muted-foreground mb-2">
                      Le 24 février 2022, le CARIA a été inauguré à Brazzaville par le Premier ministre Anatole Collinet Makosso. Ce centre, situé à l'Université Denis Sassou Nguesso à Kintele, vise à exploiter le potentiel de la recherche en IA et du développement humain pour favoriser le progrès social et économique dans le pays.
                    </p>
                  </Card>

                  <Card className="p-6">
                    <h4 className="font-bold text-lg text-foreground mb-3">Centre de Recherche Africain pour l'Intelligence Artificielle (ARCAI)</h4>
                    <p className="text-muted-foreground">
                      L'ARCAI a également été établi à Brazzaville. Ce centre se concentre sur l'avancement des technologies d'IA et la promotion de partenariats pour renforcer les capacités locales en recherche et application de l'IA.
                    </p>
                  </Card>
                </div>

                <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Défis et Barrières</h2>
                
                <h3 className="text-2xl font-semibold text-foreground mb-4 mt-8">Limitations Infrastructurelles</h3>
                <p className="text-muted-foreground mb-4 leading-relaxed">
                  Un obstacle fondamental à l'adoption de l'IA à Brazzaville est le manque d'infrastructure essentielle. L'électricité fiable et la connectivité Internet sont cruciales pour déployer les technologies d'IA, mais l'accès reste limité, en particulier dans les zones rurales. En 2023, seulement 51,3% de la population avait accès à l'électricité.
                </p>

                <h3 className="text-2xl font-semibold text-foreground mb-4 mt-8">Déficit de Compétences</h3>
                <p className="text-muted-foreground mb-4 leading-relaxed">
                  Un autre défi majeur est la pénurie de professionnels qualifiés dans le secteur de l'IA. Les pays africains, y compris ceux du Congo, ont du mal à former et à retenir des talents ayant une expertise en IA, en apprentissage automatique et en science des données. Ce déficit de compétences limite non seulement le développement d'applications d'IA mais impacte également l'évolutivité et la compétitivité des startups locales.
                </p>

                <h3 className="text-2xl font-semibold text-foreground mb-4 mt-8">Disparités Socio-Économiques</h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Les inégalités socio-économiques compliquent davantage le paysage pour l'adoption de l'IA. Les populations marginalisées et les communautés rurales manquent souvent des ressources et de l'infrastructure nécessaires pour bénéficier des technologies d'IA, élargissant le fossé numérique entre les zones urbaines et rurales.
                </p>

                <Card className="my-8 p-6 bg-yellow-500/10 border-yellow-500/30">
                  <h4 className="font-semibold mb-3 text-foreground">⚠️ Souveraineté des Données et Gouvernance</h4>
                  <p className="text-muted-foreground">
                    Une portion considérable des ensembles de données utilisés pour entraîner les systèmes d'IA en Afrique est collectée par des entités non africaines, souvent traitée en dehors du continent. Cette situation soulève des préoccupations concernant la "colonisation algorithmique", où les intérêts des grandes entreprises étrangères peuvent éclipser les besoins et priorités locaux.
                  </p>
                </Card>

                <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Opportunités de Développement</h2>
                
                <h3 className="text-2xl font-semibold text-foreground mb-4 mt-8">Initiatives Socio-Économiques</h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Des efforts pour relever les défis de développement dans des secteurs tels que l'énergie, l'eau, l'agriculture, la santé et l'éducation ont été initiés à travers des plateformes comme l'AI Hub. Ces initiatives visent à débloquer des financements et à développer des infrastructures durables, tout en promouvant l'égalité des genres et la création d'emplois.
                </p>

                <h3 className="text-2xl font-semibold text-foreground mb-4 mt-8">Éducation et Développement des Compétences</h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  L'intégration de la formation aux compétences numériques dans les programmes éducatifs est cruciale pour construire une main-d'œuvre prête pour l'avenir, capable de naviguer dans les technologies d'IA. La République du Congo a l'opportunité d'adopter des stratégies similaires à celles du Kenya, où la littératie numérique et le codage sont intégrés dans l'éducation primaire et secondaire.
                </p>

                <h3 className="text-2xl font-semibold text-foreground mb-4 mt-8">Diversification Économique</h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  L'investissement dans les secteurs non pétroliers est essentiel pour la viabilité économique à long terme du pays. Avec les prévisions de croissance continue dans les industries non pétrolières, prioriser la diversification peut aider à atténuer les défis posés par la baisse des revenus pétroliers et les taux de chômage élevés, qui se situent actuellement autour de 23%.
                </p>

                <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Études de Cas</h2>
                
                <div className="space-y-6 mb-8">
                  <Card className="p-6 border-l-4 border-l-primary">
                    <h4 className="font-bold text-lg text-foreground mb-3">🏥 IA dans la Santé</h4>
                    <p className="text-muted-foreground">
                      À Brazzaville, l'IA est utilisée pour relever des défis importants dans le secteur de la santé, particulièrement pendant la pandémie de COVID-19. Une initiative impliquait l'intégration d'algorithmes d'IA qui ont soutenu les agents de santé communautaires dans l'identification des patients nécessitant des références vers des soins de niveau supérieur.
                    </p>
                  </Card>

                  <Card className="p-6 border-l-4 border-l-green-500">
                    <h4 className="font-bold text-lg text-foreground mb-3">🌾 IA dans l'Agriculture</h4>
                    <p className="text-muted-foreground">
                      L'agriculture reste un secteur critique au Congo, employant une portion substantielle de la main-d'œuvre. Les technologies pilotées par l'IA sont mises en œuvre pour améliorer la productivité et la sécurité alimentaire. Des algorithmes de détection de maladies fonctionnant sans connectivité Internet ont été développés, les rendant particulièrement efficaces dans les environnements ruraux.
                    </p>
                  </Card>

                  <Card className="p-6 border-l-4 border-l-blue-500">
                    <h4 className="font-bold text-lg text-foreground mb-3">💰 IA pour l'Inclusion Financière</h4>
                    <p className="text-muted-foreground">
                      Le paysage financier à Brazzaville bénéficie également des innovations en IA. Une institution de microfinance en Ouganda a utilisé avec succès la notation de crédit alimentée par l'IA pour étendre des prêts aux petites entreprises qui étaient auparavant exclues des services financiers. Des approches similaires pourraient être adaptées à Brazzaville pour autonomiser les entrepreneurs locaux et soutenir la croissance économique.
                    </p>
                  </Card>

                  <Card className="p-6 border-l-4 border-l-purple-500">
                    <h4 className="font-bold text-lg text-foreground mb-3">🏙️ Engagement Communautaire dans l'Urbanisme</h4>
                    <p className="text-muted-foreground">
                      Le projet DURQuaP à Brazzaville illustre le rôle de l'IA dans la planification urbaine et l'engagement communautaire. La méthodologie inclut des promenades exploratoires et des groupes de discussion qui recueillent des données qualitatives sur les perceptions des résidents concernant les risques dans leurs quartiers.
                    </p>
                  </Card>
                </div>

                <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Perspectives d'Avenir</h2>
                
                <h3 className="text-2xl font-semibold text-foreground mb-4 mt-8">Croissance Économique et Développement des Infrastructures</h3>
                <p className="text-muted-foreground mb-4 leading-relaxed">
                  Pour exploiter pleinement l'IA pour la croissance économique, il est crucial de relever les défis infrastructurels. Le manque de centres de données suffisants, la stabilité énergétique et la faible connectivité peuvent entraver l'évolutivité des initiatives d'IA. Investir dans l'infrastructure de l'IA nécessite non seulement des ressources financières substantielles mais aussi un engagement à développer des systèmes complémentaires tels que l'approvisionnement en énergie et la gouvernance des données.
                </p>

                <h3 className="text-2xl font-semibold text-foreground mb-4 mt-8">Innovation en IA et Renforcement des Capacités Locales</h3>
                <p className="text-muted-foreground mb-4 leading-relaxed">
                  L'écosystème technologique local, bien que naissant, évolue grâce à des efforts de base et des collaborations avec des partenaires internationaux, montrant une reconnaissance croissante du potentiel transformateur de l'IA pour favoriser une croissance inclusive.
                </p>

                <h3 className="text-2xl font-semibold text-foreground mb-4 mt-8">Collaborations Stratégiques et Intégration Mondiale</h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Les collaborations avec des partenaires internationaux, telles que le mémorandum signé entre la République du Congo et l'AI Hub for Sustainable Development de l'Italie, signifient un engagement à exploiter l'IA pour une croissance durable. Ces partenariats sont essentiels pour accéder aux ressources, à l'expertise et au financement nécessaires au développement de l'IA.
                </p>

                <Card className="bg-primary/10 border-primary p-8 mb-8 mt-12">
                  <h3 className="text-2xl font-bold text-foreground mb-4">
                    Conclusion
                  </h3>
                  <p className="text-foreground leading-relaxed text-lg">
                    Le parcours vers l'adoption de l'IA à Brazzaville encapsule à la fois les aspirations à l'avancement technologique et les complexités liées aux barrières systémiques. Alors que la ville cherche à exploiter l'IA pour un développement durable, elle se trouve à un carrefour où les investissements stratégiques, l'autonomisation locale et les considérations éthiques joueront des rôles essentiels dans la formation de son avenir numérique.
                  </p>
                </Card>

                <div className="bg-muted p-8 rounded-lg text-center mt-12">
                  <h3 className="text-2xl font-bold text-foreground mb-4">
                    Intéressé par les opportunités tech à Brazzaville ?
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Découvrez les opportunités dans le secteur de la technologie et de l'innovation
                  </p>
                  <Link to="/opportunities">
                    <Button size="lg" className="font-semibold">
                      Explorer les opportunités
                    </Button>
                  </Link>
                </div>
              </>
            ) : (
              <div className="text-foreground leading-relaxed">
                <p className="text-xl mb-6">{post.excerpt}</p>
                <p>{post.content}</p>
              </div>
            )}
          </div>
        </article>

        {/* Related Articles */}
        <div className="max-w-4xl mx-auto mt-16 pt-8 border-t border-border">
          <h3 className="text-2xl font-bold text-foreground mb-6">Articles similaires</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {(() => {
              // First try to get articles from the same category
              const sameCategoryPosts = mockBlogPosts
                .filter((p) => p.category === post.category && p.id !== post.id)
                .slice(0, 2);
              
              // If we have enough articles, use them
              if (sameCategoryPosts.length >= 2) {
                return sameCategoryPosts;
              }
              
              // Otherwise, supplement with articles from other categories
              const otherCategoryPosts = mockBlogPosts
                .filter((p) => p.category !== post.category && p.id !== post.id)
                .slice(0, 2 - sameCategoryPosts.length);
              
              return [...sameCategoryPosts, ...otherCategoryPosts];
            })().map((relatedPost) => (
              <Card key={relatedPost.id} className="hover-lift">
                <Link to={`/blog/${relatedPost.slug}`} className="block p-6">
                  <div className={`inline-block ${categoryColors[relatedPost.category]} text-white px-3 py-1 rounded-md text-xs font-semibold mb-3`}>
                    {categoryLabels[relatedPost.category]}
                  </div>
                  <h4 className="font-bold text-lg text-foreground mb-2 hover:text-primary transition-colors">
                    {relatedPost.title}
                  </h4>
                  <p className="text-muted-foreground text-sm line-clamp-2">
                    {relatedPost.excerpt}
                  </p>
                </Link>
              </Card>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
